import * as Option from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { AssetPriceMap, useQueryAssetPriceMap, useQueryAssetPrices } from './useQueryAssetPrice';
import { useQueryQuotePrice } from './useQueryQuotePrice';
import { sequenceT } from 'fp-ts/lib/Apply';
import { BigIntWithDecimals, ChecksumAddress, NATIVE_CURRENCY_DECIMALS } from '@silo/types';
import { convertCurrency } from '@silo/utils';

export const usePriceInUSD = (priceInNative?: bigint) => {
  const quotePrice = useQueryQuotePrice();

  if (!quotePrice || !priceInNative) return null;

  return convertCurrency({ value: priceInNative, decimals: NATIVE_CURRENCY_DECIMALS }, quotePrice);
};

export const useAssetPricesInUSDMap = (assetAddresses: ChecksumAddress[]): AssetPriceMap => {
  const assetPrices = useQueryAssetPriceMap(assetAddresses);
  const quotePrice = useQueryQuotePrice();

  return new Map(
    Array.from(assetPrices, ([assetAddress, price]) => [
      assetAddress,
      pipe(
        /**
         * TODO: make this a reusable function (potentially 2 reusable functions that build on each other):
         * - convert array of options to option of array
         * - convert array of nullable values to option of array
         */
        sequenceT(Option.Apply)(Option.fromNullable(quotePrice), Option.fromNullable(price)),
        Option.map(([quotePrice, price]) => convertCurrency(price, quotePrice)),
        Option.toUndefined
      ),
    ])
  );
};

export const useQueryAssetPricesInUSD = (assetAddresses = [] as ChecksumAddress[], enabled = true) => {
  const { allData: assetPrices } = useQueryAssetPrices(assetAddresses, enabled);
  const quotePrice = useQueryQuotePrice(!enabled);

  if (!assetPrices || !quotePrice) return undefined;

  return assetPrices.map((assetPrice) => convertCurrency(assetPrice, quotePrice));
};

export const useQueryAssetPriceInUSD = (assetAddress?: ChecksumAddress): BigIntWithDecimals | undefined => {
  const assetPrices = useQueryAssetPricesInUSD(assetAddress ? [assetAddress] : []);

  return assetPrices ? assetPrices[0] : undefined;
};
